import { CONTRACT_TYPES } from '@vy/types';
import { fromNullToUndefined, isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { useQueryClient } from 'react-query';
import { LocalStorageKeys } from '../../../../constants/local-storage-keys';
import { useUpdateContract } from '../../../../hooks/requests/use-update-contract';
import { useValidatedParams } from '../../../../hooks/use-validated-params';
import { useVyNavigate } from '../../../../hooks/use-vy-navigate';
import { queryKeys } from '../../../../services/query-key/query-keys';
import { parsePlanInput } from '../../../../utils/parse-plan-input';
import { runDebouncedMutation } from '../../../../utils/vy-mutation';
import { PlanInputForm } from '../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { buildParentAgreementSettings } from '../../../plans/utils';

export const useUpdateContractSubmitter = () => {
  const { contractId } = useValidatedParams('contractId');
  const queryClient = useQueryClient();
  const navigate = useVyNavigate();

  const updateContractMutation = useUpdateContract();

  return async (planInput: PlanInputForm) => {
    const parsedPlan = parsePlanInput(planInput, {
      errorContext: 'Update contract validation',
    });
    const startDate = planInput.startDate;

    const isParentAgreement = planInput.contractType === CONTRACT_TYPES.ParentAgreement;
    const parentAgreementSettings = isParentAgreement ? buildParentAgreementSettings(planInput) : undefined;

    if (!parsedPlan.success || isNullOrUndefined(startDate)) {
      return;
    }

    await runDebouncedMutation(
      `update-contract-${contractId}`,
      updateContractMutation,
      {
        contractId,
        updateContractRequest: {
          plan: parsedPlan.data,
          startDate,
          currencyOverride: fromNullToUndefined(planInput.currencyOverride),
          signatureDate: fromNullToUndefined(planInput.signatureDate),
          parentAgreementSettings,
          credits: planInput?.credits,
        },
      },
      {
        pending: 'Updating...',
        success: 'Updated',
        error: (error) =>
          isNotNullOrUndefined(error?.data?.userMessage)
            ? error.data.userMessage
            : 'Failed to update contract',
      },
      {
        onSuccess: async () => {
          localStorage.removeItem(LocalStorageKeys.UpdateContract);
          navigate(`/plans/contracts/${contractId}`);

          await Promise.all([
            queryClient.invalidateQueries({
              predicate: (query) => query.queryKey.includes(contractId),
            }),

            // Invoice-related queries (invoices are linked to contracts)
            queryClient.invalidateQueries(queryKeys.invoicesOverviewsNew()),
            queryClient.invalidateQueries(queryKeys.invoicesHighlightsStatistics()),
            queryClient.invalidateQueries(queryKeys.invoiceStatistics()),
            queryClient.invalidateQueries(queryKeys.invoiceSummary()),
            queryClient.invalidateQueries(queryKeys.billingCycleInvoices()),

            // Contract lists and overviews
            queryClient.invalidateQueries(queryKeys.contracts()),
            queryClient.invalidateQueries(queryKeys.contractsOverview()),
            queryClient.invalidateQueries(queryKeys.contractsReport()),

            // Billing and revenue reports (depend on contract data)
            queryClient.invalidateQueries(queryKeys.billingCyclesOverview()),
            queryClient.invalidateQueries(queryKeys.recurringRevenueReport()),
            queryClient.invalidateQueries(queryKeys.commitmentReport()),
            queryClient.invalidateQueries(queryKeys.homePageAccountSummary()),

            // Customer overviews (contract changes affect customer data)
            queryClient.invalidateQueries(queryKeys.customersOverviewsNew()),
            queryClient.invalidateQueries(queryKeys.customerContractsOverviewPage()),

            // Credit pools (contract credits may grant/reset the pool)
            queryClient.invalidateQueries(queryKeys.creditPools()),
          ]);
        },
      },
    );
  };
};
